import * as Sentry from '@sentry/browser'
import 'axios-case-converter'
import { lazy } from 'react'
import { getMarketLink } from '../components/Navigation'
import { HomepageValue } from '../current/types'
import { matchesSome } from '../utils/array'
import { regexMatchers } from '../hooks/usePermissionsMatchers'

type ModuleDefaultExport = { default: React.ComponentType<any> }
type ReactImportPromiseGenerator = () => Promise<ModuleDefaultExport>
type ReactLazyOutput = React.LazyExoticComponent<React.ComponentType<any>>

const CHUNK_LOAD_RELOAD_KEY = 'chunkLoadReload'

const handleChunkLoadError = (error: Error) => {
  // Guard against an infinite reload loop in case the new chunk also fails.
  if (!sessionStorage.getItem(CHUNK_LOAD_RELOAD_KEY)) {
    console.log('caught frontend trying to load outdated chunk, will reload') // eslint-disable-line no-console
    sessionStorage.setItem(CHUNK_LOAD_RELOAD_KEY, '1')
    window.location.reload()
  } else {
    console.error('caught frontend trying to load outdated chunk, but reloadKey was already set', error) // eslint-disable-line no-console
    Sentry.captureException(error)
  }
}

// Returns a terminal promise if the error is unrecoverable, or null if a retry should be scheduled.
const resolveImportError = (error: Error, retriesLeft: number): Promise<ModuleDefaultExport> | null => {
  // After a deploy, old chunk URLs no longer exist. Reloading fetches fresh
  // HTML with updated asset references rather than retrying the same dead URL.
  const chromeChunkLoadError = 'Failed to fetch dynamically imported module'
  const otherChunkLoadError = 'Importing a module script failed'
  const isChunkLoadError =
    error?.message?.includes(chromeChunkLoadError) || error?.message?.includes(otherChunkLoadError)
  if (isChunkLoadError) {
    handleChunkLoadError(error)
    return new Promise<ModuleDefaultExport>(() => {}) // never resolves — reload/spinner takes over until retries are exhausted below
  }
  if (retriesLeft === 1) {
    console.error('Retries maxed out, reporting error', error) // eslint-disable-line no-console
    Sentry.captureException(error)
    return Promise.reject<ModuleDefaultExport>(error)
  }
  return null
}

function retryImport(fn: ReactImportPromiseGenerator, retriesLeft = 3, interval = 1000): Promise<ModuleDefaultExport> {
  return fn()
    .then(module => {
      if (sessionStorage.getItem(CHUNK_LOAD_RELOAD_KEY)) {
        // Successful load means the reload worked — clear the guard so future
        // deploys in this session can still trigger a reload.
        console.log('successful load, clearing reloadKey') // eslint-disable-line no-console
        sessionStorage.removeItem(CHUNK_LOAD_RELOAD_KEY)
      }
      return module
    })
    .catch((error: Error) => {
      const terminal = resolveImportError(error, retriesLeft)
      if (terminal !== null) return terminal
      return new Promise<ModuleDefaultExport>(resolve => setTimeout(resolve, interval)).then(() =>
        retryImport(fn, retriesLeft - 1, interval)
      )
    })
}

export function lazyLoadWithRetry(importFn: ReactImportPromiseGenerator): ReactLazyOutput {
  return lazy(() => retryImport(importFn))
}

interface CalculateHomeBehaviorParams {
  permissions: string[]
  homepageOverride: HomepageValue
  roleName?: string
  supplierId?: number
  userOnboarded?: boolean
  orgConfigMarkets?: any
}

export const calculateHomeBehavior = ({
  permissions,
  homepageOverride,
  roleName,
  supplierId,
  userOnboarded,
  orgConfigMarkets = null
}: CalculateHomeBehaviorParams) => {
  let homeComponentString = null
  let redirectPath = null
  const onboardedIfNecessary = userOnboarded // onboardedIfNecessary - see User.onboarded?

  const almostAllPermissionsAreReports =
    (permissions.filter(p => p.startsWith('reports.')).length * 1.0) / permissions.length > 0.9

  if (homepageOverride === 'ADMIN_DASHBOARD') {
    homeComponentString = 'AdminHome'
  } else if (homepageOverride === 'SUPPLIER_DASHBOARD') {
    homeComponentString = 'StoreAdminHome'
  } else if (homepageOverride === 'POSTING_HISTORY') {
    homeComponentString = 'ProductsCatalogHome'
  } else if (homepageOverride === 'ORDERS') {
    redirectPath = '/orders'
  } else if (homepageOverride === 'MANIFESTS') {
    redirectPath = '/manifests'
  } else if (homepageOverride === 'PRODUCTS') {
    redirectPath = '/products'
  } else if (homepageOverride === 'NEW_SHIPMENT') {
    redirectPath = '/shipping'
  } else if (homepageOverride === 'BOOKS') {
    redirectPath = '/scan-books'
  } else if (roleName?.toLowerCase() === 'admin') {
    homeComponentString = 'AdminHome'
  } else if (supplierId && permissions.includes('reports.supplier')) {
    homeComponentString = 'StoreAdminHome'
  } else if (permissions.includes('products.post')) {
    homeComponentString = 'ProductsCatalogHome'
  } else if (almostAllPermissionsAreReports) {
    redirectPath = '/reports'
  } else if (
    matchesSome(permissions, regexMatchers.anyManifestPermission) &&
    !permissions.includes('reports.supplier')
  ) {
    /* Redirect store users to manifests index. More details https://www.notion.so/uprightlabshq/Dashboard-UI-8c7e3a6f7a2743b8a48e043c7b159939 */
    redirectPath = '/manifests'
  } else if (permissions.includes('orders.all')) {
    redirectPath = '/orders'
  } else if (!onboardedIfNecessary) {
    /* catch all for not onboarded */
    redirectPath = '/onboarding/shopgoodwill'
  } else if (permissions.includes('listings.view')) {
    // This is lower priority than the orders redirect. For example, if someone primarily fulfills orders,
    // but they also have access to the listing manager, they would still want the orders page as their homepage.

    if (orgConfigMarkets) {
      const marketLinks = orgConfigMarkets.map(market => getMarketLink(market))
      const nonNullMarketLinks = marketLinks.filter(n => n)

      if (nonNullMarketLinks.length === 1) {
        // Show them the listing manager for the only channel they have connected.
        // Could enhance this to handle cases with multiple channels (derive the most appropriate listing manager
        // based on their personal data) but this set of permissions isn't a common use case.

        ;[redirectPath] = nonNullMarketLinks
      }
    }

    // ProductsCatalogHome works as a fallback.
    if (!redirectPath) {
      homeComponentString = 'ProductsCatalogHome'
    }
  } else if (permissions.includes('products.view')) {
    // If you can view the product manager AND you have some reports/settings permissions,
    // you're probably using the product manager more frequently, so prefer using it as the homepage.
    redirectPath = '/products'
  } else if (matchesSome(permissions, 'reports.*')) {
    redirectPath = '/reports'
  } else if (matchesSome(permissions, 'settings.*')) {
    redirectPath = '/admin/settings'
  } else if (permissions.includes('shipping.all')) {
    redirectPath = '/shipping'
  }

  return {
    homeComponentString,
    redirectPath
  }
}

export const clientOnNewestVersion = (clientVersion: string, serverVersion: string) => {
  // If either of these values is null we can't really
  // determine which is newer and don't want to cause random reloads
  if (!clientVersion || !serverVersion) {
    return true
  }

  if (clientVersion.length === 0 || serverVersion.length === 0) {
    return true
  }

  return clientVersion === serverVersion
}

export const nextUpdateDate = (isProd = true) => {
  const maxMinuteJitter = 5
  const jitter = Math.floor(Math.random() * maxMinuteJitter) + 1
  let minutesToWait = isProd === true ? 60 : 1
  minutesToWait += jitter
  return new Date(new Date().getTime() + minutesToWait * 60 * 1000)
}

// Make the window reload (in order to fetch the latest code) only if the client
// version does not match the server version. Add a delay and some jitter before
// reloading so people don't have to have to deal with the website reloading
// many times in short order.
//
// If we deploy many new versions of the app quickly within the
// delay, only reload the window after the delay after the _lastest_ update.
export const reloadOnNewVersionEffect = (clientVersion, serverVersion, willReloadState, setWillReloadState, isProd) => {
  const onNewest = clientOnNewestVersion(clientVersion, serverVersion)

  if (!onNewest) {
    // If serverVersion !== willReloadState.serverVersion, reset timer to one hour + jitter from now
    if (serverVersion !== willReloadState.serverVersion) {
      const nextUpdateAfterDate = nextUpdateDate(isProd)
      setWillReloadState({ serverVersion, updateAfter: nextUpdateAfterDate })
      return
    }

    if (willReloadState.updateAfter > new Date()) {
      // do nothing, we're still waiting for time to pass
    } else {
      analytics.track('Versions Reloaded Outdated Client')
      setWillReloadState({ serverVersion: willReloadState.serverVersion, updateAfter: null })
      window.location.reload()
    }
  }
}
